import { useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { bridge } from '../bridge'
import './SwipeToReply.css'

/// Message-bubble gesture wrapper (WhatsApp / Signal style):
///   • swipe RIGHT past the threshold → onReply (a reply arrow reveals from the
///     leading edge as you drag; snaps back on release).
///   • long-press (500ms, no motion) → onLongPress (the action menu).
///   • a short tap does nothing — the bubble is inert to a plain tap by design.
///   • desktop: right-click (contextmenu) → onLongPress.
///
/// Modeled on SwipeableRow's pointer-capture state machine so the two feel
/// identical. Differences: this swipes the *content* rightward (positive dx)
/// rather than revealing a trailing action, and it is axis-aware — a mostly
/// vertical drag is a scroll and is left to the browser (`touch-action: pan-y`
/// handles touch; the explicit check covers mouse, which has no touch-action
/// scrolling). Taps that land on an interactive descendant (link, code-copy
/// button, tap-to-zoom image) are left alone so those keep working.
export function SwipeToReply({
  children,
  onReply,
  onLongPress,
}: {
  children: ReactNode
  // Absent → no swipe action (e.g. inside a thread, where there's nothing to
  // reply "into"); a rightward drag is then just abandoned, like a leftward one.
  onReply?: () => void
  onLongPress: () => void
}) {
  const [dx, setDx] = useState(0)
  const [dragging, setDragging] = useState(false)
  // Mirror of dx read in the end handler. The release decision must not depend on
  // the render closure's dx (which can lag), and it must survive a pointercancel
  // arriving in place of pointerup — the fire is computed from this ref instead.
  const dxRef = useRef(0)
  const startX = useRef(0)
  const startY = useRef(0)
  const pointerId = useRef<number | null>(null)
  const captured = useRef(false)
  // Once a drag is classified as a vertical scroll we stop tracking it entirely,
  // so a diagonal wobble can't later flip it into a swipe.
  const scrolling = useRef(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const gestureFired = useRef(false)

  const threshold = 48
  const maxReveal = 96
  // Motion that elevates a press from "tap" to a drag; below it we do nothing so
  // the long-press timer can run and a tap stays a tap.
  const dragStart = 8
  const longPressMs = 500

  function cancelLongPress() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  function reset() {
    pointerId.current = null
    captured.current = false
    scrolling.current = false
    cancelLongPress()
    setDragging(false)
    setDx(0)
    dxRef.current = 0
  }

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    // Never start a gesture from an interactive child — let its own click run.
    if ((e.target as HTMLElement).closest('a, button, img, input, textarea')) return
    pointerId.current = e.pointerId
    startX.current = e.clientX
    startY.current = e.clientY
    captured.current = false
    scrolling.current = false
    gestureFired.current = false
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null
      gestureFired.current = true
      bridge.call('haptic.light').catch(() => {})
      onLongPress()
    }, longPressMs)
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (pointerId.current !== e.pointerId || scrolling.current) return
    // A hovering mouse (no button held) must never drive the row — mirrors the
    // self-healing guard in SwipeableRow (desktop has no implicit capture).
    if (e.buttons === 0) {
      reset()
      return
    }
    const deltaX = e.clientX - startX.current
    const deltaY = e.clientY - startY.current
    if (!captured.current) {
      // Vertical intent → it's a scroll; bail and let the list handle it.
      if (Math.abs(deltaY) > dragStart && Math.abs(deltaY) >= Math.abs(deltaX)) {
        scrolling.current = true
        cancelLongPress()
        return
      }
      if (deltaX < -dragStart || (!onReply && deltaX > dragStart)) {
        // Leftward swipe has no action on a bubble, and nor does a rightward
        // one when there's no reply action; abandon the gesture.
        scrolling.current = true
        cancelLongPress()
        return
      }
      if (deltaX < dragStart) return
      // Rightward drag begins — this is a swipe, not a long-press.
      cancelLongPress()
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      captured.current = true
      setDragging(true)
    }
    // Track only rightward motion, with light resistance past the threshold.
    const pulled = Math.max(0, deltaX)
    const eased = pulled > threshold ? threshold + (pulled - threshold) * 0.3 : pulled
    const next = Math.min(maxReveal, eased)
    dxRef.current = next
    setDx(next)
  }

  // Ends the gesture — bound to BOTH pointerup and pointercancel, because iOS may
  // deliver a cancel in place of the up. Fires reply when the swipe was past the
  // threshold at the moment it ended (read from the ref, never the stale closure).
  function onPointerEnd(e: PointerEvent<HTMLDivElement>) {
    if (pointerId.current !== e.pointerId) return
    pointerId.current = null
    cancelLongPress()
    const shouldFire = captured.current && dxRef.current >= threshold
    captured.current = false
    scrolling.current = false
    setDragging(false)
    setDx(0)
    dxRef.current = 0
    if (shouldFire && onReply) {
      gestureFired.current = true
      bridge.call('haptic.light').catch(() => {})
      onReply()
    }
  }

  const armed = dx > 0
  const hintOpacity = Math.min(1, dx / threshold)
  const hintScale = 0.6 + 0.4 * hintOpacity

  return (
    <div
      className="msg-swipe"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onContextMenu={(e) => {
        // Desktop affordance: right-click opens the action menu. No trailing
        // click follows a contextmenu, so we don't arm the click-swallow here
        // (doing so would stale-swallow the next legitimate click).
        e.preventDefault()
        onLongPress()
      }}
      onClickCapture={(e) => {
        // A fired gesture (long-press, right-click, or a completed swipe) still
        // has a trailing click queued; swallow it so an inner link/button doesn't
        // also activate. A plain tap never sets the flag, so its clicks pass.
        if (gestureFired.current) {
          gestureFired.current = false
          e.preventDefault()
          e.stopPropagation()
        }
      }}
    >
      <div
        className={`msg-swipe__hint${armed ? ' msg-swipe__hint--armed' : ''}`}
        style={{ opacity: hintOpacity, transform: `scale(${hintScale})` }}
        aria-hidden
      >
        ↩
      </div>
      <div
        className={`msg-swipe__content${dragging ? ' msg-swipe__content--dragging' : ''}`}
        style={{ transform: `translateX(${dx}px)` }}
      >
        {children}
      </div>
    </div>
  )
}
